import React from 'react';
import { Link2, CheckCircle2, XCircle, Clock, Shield, Key, FileText } from 'lucide-react';

interface Integration {
  system: string;
  api_available: boolean;
  connector_available: boolean;
  auth_methods: string[];
  typical_approval_time: string;
  governance_complexity: string;
  recommendation: string;
}

interface IntegrationAnalysisProps {
  integrationAnalysis: {
    total_integrations: number;
    integrations: Integration[];
  };
}

const IntegrationAnalysis: React.FC<IntegrationAnalysisProps> = ({ integrationAnalysis }) => {
  if (!integrationAnalysis || !integrationAnalysis.integrations || integrationAnalysis.integrations.length === 0) {
    return null;
  }

  const getComplexityColor = (complexity: string) => {
    if (complexity === 'low') return 'bg-green-100 text-green-700 border-green-300';
    if (complexity === 'medium') return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-red-100 text-red-700 border-red-300';
  };

  const getComplexityBadge = (complexity: string) => {
    if (complexity === 'low') return 'bg-green-500';
    if (complexity === 'medium') return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="bg-gradient-to-br from-cyan-50 to-blue-100 border-2 border-cyan-300 rounded-xl p-6 shadow-lg">
      <div className="flex items-center gap-2 mb-6">
        <Link2 className="w-6 h-6 text-cyan-600" />
        <h3 className="text-lg font-bold text-gray-900">Integration Analysis</h3>
        <span className="ml-auto px-3 py-1 bg-cyan-600 text-white rounded-full text-sm font-semibold">
          {integrationAnalysis.total_integrations} System{integrationAnalysis.total_integrations > 1 ? 's' : ''}
        </span>
      </div>

      <div className="space-y-4">
        {integrationAnalysis.integrations.map((integration, idx) => (
          <div key={idx} className="bg-white rounded-lg border-2 border-cyan-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="p-4">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{integration.system}</h4>
                  {integration.governance_complexity && (
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${getComplexityBadge(integration.governance_complexity)}`}></div>
                      <span className={`px-3 py-1 rounded-lg text-xs font-semibold border ${getComplexityColor(integration.governance_complexity)}`}>
                        {integration.governance_complexity.toUpperCase()} Governance
                      </span>
                    </div>
                  )}
                </div>
                {integration.typical_approval_time && (
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-sm text-gray-600 mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="font-semibold">{integration.typical_approval_time}</span>
                    </div>
                    <p className="text-xs text-gray-500">Typical Approval</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className={`p-3 rounded-lg border ${integration.api_available ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {integration.api_available ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                    <span className={`text-xs font-semibold ${integration.api_available ? 'text-green-700' : 'text-red-700'}`}>
                      API Access
                    </span>
                  </div>
                  <p className={`text-xs ${integration.api_available ? 'text-green-600' : 'text-red-600'}`}>
                    {integration.api_available ? 'Available' : 'Not Available'}
                  </p>
                </div>

                <div className={`p-3 rounded-lg border ${integration.connector_available ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {integration.connector_available ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-orange-600" />
                    )}
                    <span className={`text-xs font-semibold ${integration.connector_available ? 'text-green-700' : 'text-orange-700'}`}>
                      Pre-built Connector
                    </span>
                  </div>
                  <p className={`text-xs ${integration.connector_available ? 'text-green-600' : 'text-orange-600'}`}>
                    {integration.connector_available ? 'Available' : 'Custom Integration Needed'}
                  </p>
                </div>
              </div>

              {integration.auth_methods && integration.auth_methods.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Key className="w-4 h-4 text-gray-600" />
                    <span className="text-xs font-semibold text-gray-700">Authentication Methods</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {integration.auth_methods.map((method, mIdx) => (
                      <span
                        key={mIdx}
                        className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded border border-indigo-200"
                      >
                        {method}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {integration.recommendation && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-blue-900 mb-1">Recommendation:</p>
                      <p className="text-xs text-blue-800">{integration.recommendation}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-bold mb-2">Integration Strategy</h4>
            <p className="text-sm opacity-90">
              Start with systems that have pre-built connectors and low governance complexity.
              For complex integrations, begin with read-only access in Phase 1, then request write access in Phase 2 after proving ROI.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationAnalysis;
